import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface SettingsState {
  // === Audio Settings ===
  masterVolume: number; // 0-100
  musicVolume: number; // 0-100
  sfxVolume: number; // 0-100
  voiceVolume: number; // 0-100

  // === Display Settings ===
  textSpeed: number; // 0-100 (lower = slower)
  showTypewriter: boolean; // Show typewriter effect
  screenEffects: boolean; // Vignette, film grain, etc.

  // === Accessibility Settings ===
  fontSize: 'small' | 'medium' | 'large';
  highContrast: boolean;
  reducedMotion: boolean;

  // === Actions ===
  setMasterVolume: (vol: number) => void;
  setMusicVolume: (vol: number) => void;
  setSFXVolume: (vol: number) => void;
  setVoiceVolume: (vol: number) => void;

  setTextSpeed: (speed: number) => void;
  setShowTypewriter: (enabled: boolean) => void;
  setScreenEffects: (enabled: boolean) => void;

  setFontSize: (size: 'small' | 'medium' | 'large') => void;
  setHighContrast: (enabled: boolean) => void;
  setReducedMotion: (enabled: boolean) => void;

  resetToDefaults: () => void;
}

const defaultSettings = {
  // Audio
  masterVolume: 80,
  musicVolume: 70,
  sfxVolume: 80,
  voiceVolume: 90,

  // Display
  textSpeed: 50, // Medium speed
  showTypewriter: true,
  screenEffects: true,

  // Accessibility
  fontSize: 'medium' as const,
  highContrast: false,
  reducedMotion: false,
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...defaultSettings,

      // === Audio Actions ===
      setMasterVolume: (vol) => {
        const volume = Math.max(0, Math.min(100, vol));
        set({ masterVolume: volume });

        // Also update audio store
        if (window.useAudioStore) {
          window.useAudioStore.getState().setMasterVolume(volume);
        }
      },

      setMusicVolume: (vol) => {
        const volume = Math.max(0, Math.min(100, vol));
        set({ musicVolume: volume });

        if (window.useAudioStore) {
          window.useAudioStore.getState().setMusicVolume(volume);
        }
      },

      setSFXVolume: (vol) => {
        const volume = Math.max(0, Math.min(100, vol));
        set({ sfxVolume: volume });

        if (window.useAudioStore) {
          window.useAudioStore.getState().setSFXVolume(volume);
        }
      },

      setVoiceVolume: (vol) => {
        const volume = Math.max(0, Math.min(100, vol));
        set({ voiceVolume: volume });

        if (window.useAudioStore) {
          window.useAudioStore.getState().setVoiceVolume(volume);
        }
      },

      // === Display Actions ===
      setTextSpeed: (speed) => {
        set({ textSpeed: Math.max(0, Math.min(100, speed)) });
      },

      setShowTypewriter: (enabled) => {
        set({ showTypewriter: enabled });
      },

      setScreenEffects: (enabled) => {
        set({ screenEffects: enabled });
      },

      // === Accessibility Actions ===
      setFontSize: (size) => {
        set({ fontSize: size });

        // Update CSS variable
        const root = document.documentElement;
        const sizes = {
          small: '0.875rem',
          medium: '1rem',
          large: '1.25rem',
        };
        root.style.setProperty('--dialogue-font-size', sizes[size]);
      },

      setHighContrast: (enabled) => {
        set({ highContrast: enabled });

        // Toggle high contrast class on body
        if (enabled) {
          document.body.classList.add('high-contrast');
        } else {
          document.body.classList.remove('high-contrast');
        }
      },

      setReducedMotion: (enabled) => {
        set({ reducedMotion: enabled });

        // Update CSS variable
        const root = document.documentElement;
        root.style.setProperty('--animation-duration', enabled ? '0.01ms' : '300ms');
      },

      // === Utility ===
      resetToDefaults: () => {
        set(defaultSettings);

        // Reset CSS modifications
        document.body.classList.remove('high-contrast');
        const root = document.documentElement;
        root.style.setProperty('--dialogue-font-size', '1rem');
        root.style.setProperty('--animation-duration', '300ms');
      },
    }),
    {
      name: 'nordicneve-settings', // localStorage key
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Extend window for TypeScript
declare global {
  interface Window {
    useAudioStore?: any;
  }
}
